import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  ShieldCheck,
  Wallet,
  Coins,
  Package,
  Boxes,
  ShoppingCart,
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
  Ban,
  Eye,
  ArrowRight,
  ArrowUpRight,
  ArrowDownLeft,
  IndianRupee,
  RefreshCw,
  PlusCircle,
  Layers,
  Sparkles,
  AlertTriangle,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StandardImage } from "@/components/shared";
import { channelOrdersService, type ChannelOrder } from "@/services/channelOrdersService";
import { channelStockService, type ChannelStockMine } from "@/services/channelStockService";
import { channelPartiesService } from "@/services/channelPartiesService";
import { useChannelDesk } from "@/lib/channel-desk";
import { useAuth } from "@/contexts/AuthContext";
import { useMyWallet } from "@/hooks/queries/useWallets";

function formatInr(amount: number) {
  return `₹${Number(amount || 0).toLocaleString("en-IN")}`;
}

function formatDate(dateStr?: string) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function formatDateTime(dateStr?: string) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

function getStatusBadge(statusText: string) {
  switch (statusText?.toLowerCase()) {
    case "approved":
      return (
        <Badge
          variant="outline"
          className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20 text-xs gap-1 font-semibold"
        >
          <CheckCircle2 className="h-3 w-3" />
          Approved
        </Badge>
      );
    case "rejected":
      return (
        <Badge
          variant="outline"
          className="bg-destructive/10 text-destructive border-destructive/20 text-xs gap-1 font-semibold"
        >
          <XCircle className="h-3 w-3" />
          Rejected
        </Badge>
      );
    case "cancelled":
      return (
        <Badge
          variant="outline"
          className="bg-muted text-muted-foreground border-border text-xs gap-1 font-semibold"
        >
          <Ban className="h-3 w-3" />
          Cancelled
        </Badge>
      );
    case "pending":
    default:
      return (
        <Badge
          variant="outline"
          className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30 text-xs gap-1 font-semibold"
        >
          <Clock className="h-3 w-3" />
          Pending
        </Badge>
      );
  }
}

const DistributorDashboard = () => {
  const desk = useChannelDesk();
  const { user } = useAuth();
  const [selectedOrder, setSelectedOrder] = useState<ChannelOrder | null>(null);

  const profile = useQuery({
    queryKey: ["channel", "me"],
    queryFn: () => channelPartiesService.getMine(),
  });
  const stock = useQuery({
    queryKey: ["channel", "stock"],
    queryFn: () => channelStockService.getMine(),
  });
  const incoming = useQuery({
    queryKey: ["channel", "orders", "incoming", "party"],
    queryFn: () => channelOrdersService.incoming(),
  });
  const outgoing = useQuery({
    queryKey: ["channel", "orders", "outgoing"],
    queryFn: () => channelOrdersService.outgoing(),
  });
  const commission = useQuery({
    queryKey: ["channel", "commission"],
    queryFn: () => channelOrdersService.commission(),
  });
  const wallet = useMyWallet();

  const isLoading =
    profile.isLoading ||
    stock.isLoading ||
    incoming.isLoading ||
    outgoing.isLoading ||
    commission.isLoading;

  const stockItems: ChannelStockMine[] = stock.data ?? [];
  const incomingOrders: ChannelOrder[] = incoming.data ?? [];
  const outgoingOrders: ChannelOrder[] = outgoing.data ?? [];

  // Stock calculations
  const totalStockUnits = stockItems.reduce((sum, row) => sum + row.quantity, 0);
  const totalStockBv = stockItems.reduce(
    (sum, row) => sum + row.quantity * (row.businessVolume ?? 0),
    0,
  );
  const totalStockValue = stockItems.reduce(
    (sum, row) =>
      sum +
      row.quantity *
        (row.price || row.distributorPrice || row.wholesalePrice || 0),
    0,
  );
  const lowStockItems = stockItems.filter(
    (item) => item.quantity <= (item.lowStockThreshold || 5),
  );

  // Orders calculations
  const totalOrdersCount = incomingOrders.length + outgoingOrders.length;
  const pendingIn = incomingOrders.filter((o) => o.statusText === "Pending").length;
  const pendingOut = outgoingOrders.filter((o) => o.statusText === "Pending").length;
  const totalPending = pendingIn + pendingOut;

  const completedIn = incomingOrders.filter((o) => o.statusText === "Approved").length;
  const completedOut = outgoingOrders.filter((o) => o.statusText === "Approved").length;
  const totalCompleted = completedIn + completedOut;

  const totalSalesRevenue = outgoingOrders
    .filter((o) => o.statusText === "Approved")
    .reduce((sum, o) => sum + (o.totalAmount || o.quantity * o.unitPrice), 0);
  const totalPurchaseSpend = incomingOrders
    .filter((o) => o.statusText === "Approved")
    .reduce((sum, o) => sum + (o.totalAmount || o.quantity * o.unitPrice), 0);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-44 w-full rounded-2xl" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    );
  }

  const partyData = profile.data;
  const businessName =
    partyData?.businessName || user?.name || `${desk.role} Partner`;
  const contactName = partyData?.contactName || user?.name || "Business Owner";
  const memberId = partyData?.id ? `#${desk.role.toUpperCase().slice(0, 4)}-${partyData.id}` : user?.username || "—";
  const mobile = partyData?.phone || user?.phone || "—";
  const email = partyData?.email || user?.email || "—";
  const address = partyData?.city || "—";
  const joinDate = formatDate(partyData?.createdAt);
  const accountStatus = partyData?.statusText || "Active";
  const commissionBalance = commission.data?.balance ?? 0;
  const walletBalance = wallet.data?.balance ?? 0;

  return (
    <div className="space-y-6">
      {/* 1. PROFILE / BUSINESS INFORMATION CARD */}
      <Card className="overflow-hidden border-border/60 bg-card shadow-sm">
        <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6 sm:p-7 border-b border-border/40">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start sm:items-center gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-md shadow-primary/20">
                <Building2 className="h-8 w-8" />
              </div>
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
                    {businessName}
                  </h1>
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 font-semibold">
                    {desk.role}
                  </Badge>
                  <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 font-medium">
                    <ShieldCheck className="h-3 w-3 mr-1" />
                    {accountStatus}
                  </Badge>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {desk.summary}
                </p>
                {partyData?.parentBusinessName && (
                  <p className="text-xs font-medium text-primary">
                    Upline Partner: <span className="font-semibold">{partyData.parentBusinessName}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Quick Action Buttons */}
            <div className="flex flex-wrap items-center gap-2.5 pt-2 lg:pt-0">
              <Button asChild className="gap-2 shadow-sm font-semibold">
                <Link to={`${desk.base}/catalogue`}>
                  <ShoppingCart className="h-4 w-4" />
                  Order Stock
                </Link>
              </Button>
              <Button asChild variant="outline" className="gap-2">
                <Link to={`${desk.base}/stock`}>
                  <Package className="h-4 w-4" />
                  Stock
                </Link>
              </Button>
              <Button asChild variant="outline" className="gap-2">
                <Link to={`${desk.base}/orders`}>
                  <Boxes className="h-4 w-4" />
                  Orders
                </Link>
              </Button>
              <Button asChild variant="secondary" className="gap-2">
                <Link to={`${desk.base}/commission`}>
                  <Wallet className="h-4 w-4 text-primary" />
                  Commission
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Profile Details Grid */}
        <CardContent className="p-5 sm:p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 divide-y sm:divide-y-0 sm:divide-x divide-border/40">
            <div className="space-y-1 sm:px-3 pt-2 sm:pt-0 first:pl-0">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                <User className="h-3.5 w-3.5 text-muted-foreground" />
                Contact Name
              </span>
              <p className="text-sm font-semibold text-foreground truncate" title={contactName}>
                {contactName}
              </p>
            </div>

            <div className="space-y-1 sm:px-3 pt-2 sm:pt-0">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
                Member ID
              </span>
              <p className="text-sm font-semibold text-foreground font-mono truncate" title={memberId}>
                {memberId}
              </p>
            </div>

            <div className="space-y-1 sm:px-3 pt-2 sm:pt-0">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                Mobile Number
              </span>
              <p className="text-sm font-semibold text-foreground truncate" title={mobile}>
                {mobile}
              </p>
            </div>

            <div className="space-y-1 sm:px-3 pt-2 sm:pt-0">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                Email Address
              </span>
              <p className="text-sm font-semibold text-foreground truncate" title={email}>
                {email}
              </p>
            </div>

            <div className="space-y-1 sm:px-3 pt-2 sm:pt-0">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                City / Location
              </span>
              <p className="text-sm font-semibold text-foreground truncate" title={address}>
                {address}
              </p>
            </div>

            <div className="space-y-1 sm:px-3 pt-2 sm:pt-0 last:pr-0">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                Joined On
              </span>
              <p className="text-sm font-semibold text-foreground truncate" title={joinDate}>
                {joinDate}
              </p>
            </div>
          </div>

          {/* Quick Balance Banner */}
          <div className="mt-5 pt-4 border-t border-border/50 flex flex-wrap items-center justify-between gap-3 text-xs sm:text-sm">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 rounded-lg bg-emerald-500/10 px-3 py-1.5 text-emerald-700 dark:text-emerald-400 font-medium">
                <IndianRupee className="h-4 w-4" />
                <span>Commission Balance:</span>
                <strong className="text-base font-bold">{formatInr(commissionBalance)}</strong>
              </div>
              <div className="flex items-center gap-2 rounded-lg bg-primary/10 px-3 py-1.5 text-primary font-medium">
                <Wallet className="h-4 w-4" />
                <span>Wallet Balance:</span>
                <strong className="text-base font-bold">{formatInr(walletBalance)}</strong>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {user?.referralCode && (
                <span className="rounded bg-muted px-2 py-1 font-mono">
                  Referral: <strong className="text-foreground">{user.referralCode}</strong>
                </span>
              )}
              <span className="rounded bg-muted px-2 py-1">
                Role: <strong className="text-foreground">{desk.role}</strong>
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 2. DASHBOARD SUMMARY CARDS (KPIs) */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {/* Total Orders */}
        <Link to={`${desk.base}/orders`} className="group block focus:outline-none">
          <Card className="h-full transition-all hover:border-primary/50 hover:shadow-md">
            <CardContent className="p-4 sm:p-5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Total Orders</span>
                <div className="rounded-lg bg-primary/10 p-2 text-primary group-hover:scale-110 transition-transform">
                  <ShoppingCart className="h-4 w-4" />
                </div>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold tracking-tight text-foreground">
                  {totalOrdersCount}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {incomingOrders.length} In • {outgoingOrders.length} Out
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Incoming Orders (Sales to Downline) */}
        <Link to={`${desk.base}/orders`} className="group block focus:outline-none">
          <Card className="h-full transition-all hover:border-primary/50 hover:shadow-md">
            <CardContent className="p-4 sm:p-5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Incoming Orders</span>
                <div className="rounded-lg bg-blue-500/10 p-2 text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform">
                  <ArrowDownLeft className="h-4 w-4" />
                </div>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold tracking-tight text-foreground">
                  {incomingOrders.length}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {pendingIn > 0 ? (
                    <span className="font-semibold text-amber-600 dark:text-amber-400">
                      {pendingIn} pending approval
                    </span>
                  ) : (
                    "All reviewed"
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Outgoing Orders (Purchases from Upline) */}
        <Link to={`${desk.base}/orders`} className="group block focus:outline-none">
          <Card className="h-full transition-all hover:border-primary/50 hover:shadow-md">
            <CardContent className="p-4 sm:p-5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Outgoing Orders</span>
                <div className="rounded-lg bg-purple-500/10 p-2 text-purple-600 dark:text-purple-400 group-hover:scale-110 transition-transform">
                  <ArrowUpRight className="h-4 w-4" />
                </div>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold tracking-tight text-foreground">
                  {outgoingOrders.length}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {pendingOut > 0 ? (
                    <span className="text-muted-foreground">
                      {pendingOut} waiting upline
                    </span>
                  ) : (
                    "Fulfilled"
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Available Stock */}
        <Link to={`${desk.base}/stock`} className="group block focus:outline-none">
          <Card className="h-full transition-all hover:border-primary/50 hover:shadow-md">
            <CardContent className="p-4 sm:p-5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Available Stock</span>
                <div className="rounded-lg bg-emerald-500/10 p-2 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform">
                  <Package className="h-4 w-4" />
                </div>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold tracking-tight text-foreground">
                  {totalStockUnits.toLocaleString()}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {stockItems.length} product lines
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Pending Orders (Needs Attention) */}
        <Link to={`${desk.base}/orders`} className="group block focus:outline-none">
          <Card className="h-full transition-all hover:border-amber-500/50 hover:shadow-md">
            <CardContent className="p-4 sm:p-5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Pending Orders</span>
                <div className="rounded-lg bg-amber-500/10 p-2 text-amber-600 dark:text-amber-400 group-hover:scale-110 transition-transform">
                  <Clock className="h-4 w-4" />
                </div>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold tracking-tight text-foreground">
                  {totalPending}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {pendingIn} to approve • {pendingOut} upline
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Completed Orders */}
        <Link to={`${desk.base}/orders`} className="group block focus:outline-none">
          <Card className="h-full transition-all hover:border-emerald-500/50 hover:shadow-md">
            <CardContent className="p-4 sm:p-5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Completed Orders</span>
                <div className="rounded-lg bg-emerald-500/10 p-2 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold tracking-tight text-foreground">
                  {totalCompleted}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {completedIn} sales • {completedOut} stock
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Secondary Financial & Inventory Highlights */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-border/60 bg-card">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Total Sales Revenue</p>
              <p className="text-lg font-bold text-foreground mt-0.5">{formatInr(totalSalesRevenue)}</p>
              <p className="text-[11px] text-muted-foreground">Approved channel sales</p>
            </div>
            <div className="rounded-xl bg-primary/10 p-2.5 text-primary">
              <TrendingUp className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60 bg-card">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Total Purchase Spend</p>
              <p className="text-lg font-bold text-foreground mt-0.5">{formatInr(totalPurchaseSpend)}</p>
              <p className="text-[11px] text-muted-foreground">Stock bought from upline</p>
            </div>
            <div className="rounded-xl bg-purple-500/10 p-2.5 text-purple-600 dark:text-purple-400">
              <Boxes className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60 bg-card">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Stock Valuation</p>
              <p className="text-lg font-bold text-foreground mt-0.5">{formatInr(totalStockValue)}</p>
              <p className="text-[11px] text-muted-foreground">{totalStockBv.toFixed(0)} Total BV on hand</p>
            </div>
            <div className="rounded-xl bg-emerald-500/10 p-2.5 text-emerald-600 dark:text-emerald-400">
              <Coins className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60 bg-card">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Commission Balance</p>
              <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">
                {formatInr(commissionBalance)}
              </p>
              <Link
                to={`${desk.base}/commission`}
                className="text-[11px] text-primary hover:underline inline-flex items-center gap-0.5 mt-0.5"
              >
                View Commission Ledger <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="rounded-xl bg-emerald-500/10 p-2.5 text-emerald-600 dark:text-emerald-400">
              <Wallet className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 4. RECENT ORDERS SECTION */}
      <Card className="border-border/60 shadow-sm">
        <CardHeader className="pb-3 border-b border-border/40">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-lg font-bold tracking-tight">Recent Orders</CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Latest sales and stock purchases across your channel network.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" asChild className="gap-1.5 self-start sm:self-auto">
              <Link to={`${desk.base}/orders`}>
                View All Orders
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <Tabs defaultValue="incoming" className="w-full">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <TabsList className="grid w-full sm:w-auto grid-cols-3">
                <TabsTrigger value="incoming" className="text-xs sm:text-sm">
                  Incoming Orders ({incomingOrders.length})
                </TabsTrigger>
                <TabsTrigger value="outgoing" className="text-xs sm:text-sm">
                  Outgoing Purchases ({outgoingOrders.length})
                </TabsTrigger>
                <TabsTrigger value="stock-alerts" className="text-xs sm:text-sm">
                  Stock Alerts ({lowStockItems.length})
                </TabsTrigger>
              </TabsList>
            </div>

            {/* TAB 1: INCOMING ORDERS */}
            <TabsContent value="incoming" className="m-0 space-y-4">
              {incomingOrders.length === 0 ? (
                <div className="py-12 text-center">
                  <ShoppingCart className="mx-auto h-10 w-10 text-muted-foreground/50" />
                  <p className="mt-2 text-sm font-medium text-foreground">No incoming orders yet</p>
                  <p className="text-xs text-muted-foreground">
                    When your downline channel partners place orders, they will appear here for approval.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-muted/50 text-xs font-semibold uppercase text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3">Order ID</th>
                        <th className="px-4 py-3">Product</th>
                        <th className="px-4 py-3">Buyer / Downline</th>
                        <th className="px-4 py-3 text-center">Qty</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3 text-center">Status</th>
                        <th className="px-4 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {incomingOrders.slice(0, 7).map((order) => (
                        <tr key={order.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3 font-mono font-semibold text-primary">
                            #{order.id}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5">
                              <StandardImage
                                src={order.productImageUrl}
                                alt={order.productName}
                                className="h-9 w-9 shrink-0 rounded-md border border-border object-cover"
                              />
                              <div className="min-w-0">
                                <p className="font-medium text-foreground truncate max-w-[180px] sm:max-w-[220px]">
                                  {order.productName}
                                </p>
                                {order.productSku && (
                                  <p className="text-[11px] text-muted-foreground font-mono">
                                    SKU: {order.productSku}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-foreground">{order.buyerName}</p>
                              <p className="text-xs text-muted-foreground">{order.buyerLevel || "Partner"}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center font-medium">
                            {order.quantity}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-foreground">
                            {formatInr(order.totalAmount || order.quantity * order.unitPrice)}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                            {formatDateTime(order.createdAt)}
                          </td>
                          <td className="px-4 py-3 text-center whitespace-nowrap">
                            {getStatusBadge(order.statusText)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 gap-1 text-xs"
                              onClick={() => setSelectedOrder(order)}
                            >
                              <Eye className="h-3.5 w-3.5" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            {/* TAB 2: OUTGOING ORDERS */}
            <TabsContent value="outgoing" className="m-0 space-y-4">
              {outgoingOrders.length === 0 ? (
                <div className="py-12 text-center">
                  <Boxes className="mx-auto h-10 w-10 text-muted-foreground/50" />
                  <p className="mt-2 text-sm font-medium text-foreground">No purchase orders placed</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Order products from your supplier catalogue to replenish inventory.
                  </p>
                  <Button asChild size="sm">
                    <Link to={`${desk.base}/catalogue`}>Browse Catalogue</Link>
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-muted/50 text-xs font-semibold uppercase text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3">Order ID</th>
                        <th className="px-4 py-3">Product</th>
                        <th className="px-4 py-3">Supplier</th>
                        <th className="px-4 py-3 text-center">Qty</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3 text-center">Status</th>
                        <th className="px-4 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {outgoingOrders.slice(0, 7).map((order) => (
                        <tr key={order.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3 font-mono font-semibold text-primary">
                            #{order.id}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5">
                              <StandardImage
                                src={order.productImageUrl}
                                alt={order.productName}
                                className="h-9 w-9 shrink-0 rounded-md border border-border object-cover"
                              />
                              <div className="min-w-0">
                                <p className="font-medium text-foreground truncate max-w-[180px] sm:max-w-[220px]">
                                  {order.productName}
                                </p>
                                {order.productSku && (
                                  <p className="text-[11px] text-muted-foreground font-mono">
                                    SKU: {order.productSku}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-foreground">{order.sellerName || "Admin"}</p>
                              <p className="text-xs text-muted-foreground">{order.sellerLevel || "Supplier"}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center font-medium">
                            {order.quantity}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-foreground">
                            {formatInr(order.totalAmount || order.quantity * order.unitPrice)}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                            {formatDateTime(order.createdAt)}
                          </td>
                          <td className="px-4 py-3 text-center whitespace-nowrap">
                            {getStatusBadge(order.statusText)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 gap-1 text-xs"
                              onClick={() => setSelectedOrder(order)}
                            >
                              <Eye className="h-3.5 w-3.5" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            {/* TAB 3: STOCK ALERTS */}
            <TabsContent value="stock-alerts" className="m-0 space-y-4">
              {lowStockItems.length === 0 ? (
                <div className="py-12 text-center">
                  <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500/60" />
                  <p className="mt-2 text-sm font-medium text-foreground">All inventory is well stocked</p>
                  <p className="text-xs text-muted-foreground">
                    No products currently below their minimum threshold.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-muted/50 text-xs font-semibold uppercase text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3">Product</th>
                        <th className="px-4 py-3">Category</th>
                        <th className="px-4 py-3 text-center">Available Stock</th>
                        <th className="px-4 py-3 text-right">Price</th>
                        <th className="px-4 py-3 text-center">Status</th>
                        <th className="px-4 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {lowStockItems.slice(0, 7).map((item) => (
                        <tr key={item.productId} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5">
                              <StandardImage
                                src={item.imageUrl}
                                alt={item.productName}
                                className="h-9 w-9 shrink-0 rounded-md border border-border object-cover"
                              />
                              <div>
                                <p className="font-medium text-foreground truncate max-w-[200px]">
                                  {item.productName}
                                </p>
                                {item.sku && (
                                  <p className="text-[11px] text-muted-foreground font-mono">
                                    SKU: {item.sku}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground text-xs">
                            {item.categoryName || "—"}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge
                              variant="outline"
                              className={
                                item.quantity === 0
                                  ? "bg-destructive/10 text-destructive border-destructive/20 font-bold"
                                  : "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 font-bold"
                              }
                            >
                              {item.quantity === 0 ? "Out of Stock" : `${item.quantity} Units Left`}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold">
                            {formatInr(item.price || item.distributorPrice || item.wholesalePrice)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge variant="outline" className="text-xs">
                              {item.status || "Active"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button asChild size="sm" variant="default" className="h-8 gap-1 text-xs">
                              <Link to={`${desk.base}/catalogue/place-order/${item.productId}`}>
                                <PlusCircle className="h-3.5 w-3.5" />
                                Reorder
                              </Link>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* ORDER DETAILS MODAL DIALOG */}
      <Dialog open={selectedOrder !== null} onOpenChange={(open) => !open && setSelectedOrder(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center justify-between pr-4">
              <DialogTitle className="text-lg">Order #{selectedOrder?.id}</DialogTitle>
              {selectedOrder && getStatusBadge(selectedOrder.statusText)}
            </div>
            <DialogDescription>
              Placed on {formatDateTime(selectedOrder?.createdAt)}
            </DialogDescription>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-4 py-2 text-sm">
              {/* Product Card */}
              <div className="flex items-center gap-3 rounded-lg border border-border p-3 bg-muted/20">
                <StandardImage
                  src={selectedOrder.productImageUrl}
                  alt={selectedOrder.productName}
                  className="h-14 w-14 shrink-0 rounded-lg border border-border object-cover"
                />
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-foreground truncate">{selectedOrder.productName}</h4>
                  {selectedOrder.productSku && (
                    <p className="text-xs text-muted-foreground font-mono">SKU: {selectedOrder.productSku}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-xs">
                    <span>
                      Qty: <strong>{selectedOrder.quantity}</strong>
                    </span>
                    <span>
                      Unit Price: <strong>{formatInr(selectedOrder.unitPrice)}</strong>
                    </span>
                    <span>
                      BV: <strong>{selectedOrder.businessVolume ?? 0}</strong>
                    </span>
                  </div>
                </div>
              </div>

              {/* Order Parties */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="rounded-lg border border-border p-3">
                  <span className="text-muted-foreground block font-medium">Buyer (Downline)</span>
                  <p className="font-semibold text-foreground mt-0.5">{selectedOrder.buyerName}</p>
                  <p className="text-muted-foreground">{selectedOrder.buyerLevel || "Partner"}</p>
                  {selectedOrder.buyerPhone && (
                    <p className="text-muted-foreground mt-0.5">{selectedOrder.buyerPhone}</p>
                  )}
                </div>
                <div className="rounded-lg border border-border p-3">
                  <span className="text-muted-foreground block font-medium">Seller (Supplier)</span>
                  <p className="font-semibold text-foreground mt-0.5">{selectedOrder.sellerName || "Admin"}</p>
                  <p className="text-muted-foreground">{selectedOrder.sellerLevel || "Supplier"}</p>
                  {selectedOrder.sellerPhone && (
                    <p className="text-muted-foreground mt-0.5">{selectedOrder.sellerPhone}</p>
                  )}
                </div>
              </div>

              {/* Financial Breakdown */}
              <div className="rounded-lg bg-muted/40 p-3 space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-medium">
                    {formatInr(selectedOrder.totalAmount || selectedOrder.quantity * selectedOrder.unitPrice)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Commission Earned</span>
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                    {formatInr(selectedOrder.commissionAmount || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment Method</span>
                  <span className="font-medium">{selectedOrder.paymentMethodText || "P2P"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment Status</span>
                  <span className="font-medium">{selectedOrder.paymentStatusText || "Pending"}</span>
                </div>
                {selectedOrder.paymentReference && (
                  <div className="flex justify-between font-mono">
                    <span className="text-muted-foreground">Ref / UTR</span>
                    <span>{selectedOrder.paymentReference}</span>
                  </div>
                )}
                {selectedOrder.note && (
                  <div className="pt-1 border-t border-border/50 text-muted-foreground">
                    <strong>Note:</strong> {selectedOrder.note}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="flex sm:justify-between items-center gap-2">
            <Button variant="outline" onClick={() => setSelectedOrder(null)}>
              Close
            </Button>
            <Button asChild>
              <Link to={`${desk.base}/orders`}>
                Open in Orders Panel
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DistributorDashboard;
